import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import ImageCarousel from "../components/ImageCarousal";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import AGENTS from "../data/agents";
import {
  ArrowLeft, MapPin, Bed, Bath, Maximize, Home as HomeIcon,
  Heart, Share2, Phone, Mail, X, CheckCircle,
} from "lucide-react";
import "../styles/PropertyDetails.css";

// Fix leaflet default icon (same approach as MapView.jsx)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const API = `${process.env.REACT_APP_API_URL}` || "http://localhost:8000";

function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [property, setProperty]             = useState(null);
  const [isFavorite, setIsFavorite]         = useState(false);
  const [visitDate, setVisitDate]           = useState("");
  const [scheduledDate, setScheduledDate]   = useState("");
  const [visitSuccess, setVisitSuccess]     = useState(false);
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [similarProperties, setSimilarProperties] = useState([]);

  const agentPanelRef = useRef(null);

  // ── Fetch property + similar ──────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${API}/api/properties/${id}`, { cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        const prop = data.data;
        setProperty(prop);

        // fetch similar (same city, exclude current)
        if (prop?.city) {
          fetch(`${API}/properties`)
            .then((r) => r.json())
            .then((allData) => {
              const all = allData.data || [];
              const similar = all
                .filter(
                  (p) =>
                    p._id !== id &&
                    p.city &&
                    p.city.toLowerCase() === prop.city.toLowerCase()
                )
                .slice(0, 4);
              setSimilarProperties(similar);
            })
            .catch(() => {});
        }
      })
      .catch((err) => console.error("Error fetching property:", err));

    if (token) {
      fetch(`${API}/api/auth/saved-properties`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setIsFavorite(data.properties.some((p) => p._id === id));
          }
        })
        .catch(() => {});
    }
  }, [id]);

  // ── Auto-dismiss toast after 4s ───────────────────────────────
  useEffect(() => {
    if (visitSuccess) {
      const t = setTimeout(() => setVisitSuccess(false), 4000);
      return () => clearTimeout(t);
    }
  }, [visitSuccess]);

  // ── Close agent panel on outside click ───────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (agentPanelRef.current && !agentPanelRef.current.contains(e.target)) {
        setShowAgentPanel(false);
      }
    };
    if (showAgentPanel) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showAgentPanel]);

  // ── Helpers ───────────────────────────────────────────────────
  const formatPrice = (price) => {
    if (!price) return "—";
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000)   return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const purposeBadgeClass = (p) =>
    ({ rent: "pd-badge-rent", sell: "pd-badge-sell", buy: "pd-badge-buy" }[p] || "pd-badge-rent");

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: property.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const handleScheduleVisit = () => {
    if (!visitDate) return;
    setScheduledDate(visitDate);
    setVisitDate("");
    setVisitSuccess(true);
  };

  const handleSendEmail = () => {
    window.location.href = `mailto:contact@geostate.com?subject=${encodeURIComponent(
      `Property Inquiry: ${property?.title || ""}`
    )}`;
  };

  const handleToggleFavorite = async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    const res  = await fetch(`${API}/api/auth/save-property/${id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success) setIsFavorite(data.saved);
  };

  // Agents matching property city
  const cityAgents = property?.city
    ? AGENTS.filter((a) => a.city.toLowerCase() === property.city.toLowerCase())
    : [];

  // ── Loading ───────────────────────────────────────────────────
  if (!property) {
    return (
      <div className="pd-loading">
        <div className="pd-spinner" />
        <p>Loading property details…</p>
      </div>
    );
  }

  const coords = property.location?.coordinates; // [lng, lat]
  const lat    = coords?.[1];
  const lng    = coords?.[0];

  // ─────────────────────────────────────────────────────────────
  return (
    <div className="pd-page">

      {/* ── Toast notification ── */}
      {visitSuccess && (
        <div className="pd-toast">
          <CheckCircle size={16} className="pd-toast-icon" />
          <span>
            Visit scheduled for{" "}
            {new Date(scheduledDate).toLocaleDateString("en-IN", {
              day: "numeric", month: "long", year: "numeric",
            })}
          </span>
          <button className="pd-toast-close" onClick={() => setVisitSuccess(false)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Sticky header ── */}
      <div className="pd-header">
        <button className="pd-back-btn" onClick={() => navigate("/home")}>
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <div className="pd-header-meta">
          <span className="pd-header-price">{formatPrice(property.price)}</span>
          {property.purpose === "rent" && <span className="pd-header-period">/mo</span>}
          <span className="pd-header-divider">·</span>
          <span className="pd-header-title">{property.title}</span>
        </div>

        <div className="pd-header-actions">
          <button className="pd-action-btn" onClick={handleShare}>
            <Share2 size={16} /> Share
          </button>
          <button
            className={`pd-action-btn ${isFavorite ? "pd-action-active" : ""}`}
            onClick={handleToggleFavorite}
          >
            <Heart size={16} fill={isFavorite ? "currentColor" : "none"} />
            {isFavorite ? "Saved" : "Save"}
          </button>
        </div>
      </div>

      {/* ── Full-width image carousel ── */}
      <div className="pd-carousel">
        <ImageCarousel images={property.images} />
      </div>

      {/* ── Two-column body ── */}
      <div className="pd-body">

        {/* ════ MAIN COLUMN ════ */}
        <div className="pd-main">

          {/* Title + Price + Location */}
          <div className="pd-section pd-title-section">
            <span className={`pd-badge ${purposeBadgeClass(property.purpose)}`}>
              FOR {property.purpose?.toUpperCase()}
            </span>
            <h1 className="pd-title">{property.title}</h1>
            {property.city && (
              <div className="pd-location">
                <MapPin size={14} />
                <span>{property.city}</span>
              </div>
            )}
            <div className="pd-price-row">
              <span className="pd-price">{formatPrice(property.price)}</span>
              {property.purpose === "rent" && (
                <span className="pd-price-period">/month</span>
              )}
            </div>
          </div>

          {/* Key stats row */}
          <div className="pd-section pd-stats-row">
            {property.bedrooms && (
              <div className="pd-stat">
                <Bed size={20} className="pd-stat-icon" />
                <span className="pd-stat-val">{property.bedrooms}</span>
                <span className="pd-stat-label">Bedrooms</span>
              </div>
            )}
            {property.bathrooms && (
              <div className="pd-stat">
                <Bath size={20} className="pd-stat-icon" />
                <span className="pd-stat-val">{property.bathrooms}</span>
                <span className="pd-stat-label">Bathrooms</span>
              </div>
            )}
            {property.area && (
              <div className="pd-stat">
                <Maximize size={20} className="pd-stat-icon" />
                <span className="pd-stat-val">{property.area.toLocaleString()}</span>
                <span className="pd-stat-label">sq.ft</span>
              </div>
            )}
            <div className="pd-stat">
              <HomeIcon size={20} className="pd-stat-icon" />
              <span className="pd-stat-val pd-capitalize">{property.propertyType}</span>
              <span className="pd-stat-label">Type</span>
            </div>
          </div>

          {/* More Details grid — MagicBricks style */}
          <div className="pd-section">
            <h2 className="pd-section-title">More Details</h2>
            <div className="pd-details-grid">
              <div className="pd-detail-row">
                <span className="pd-detail-label">Property Type</span>
                <span className="pd-detail-val pd-capitalize">{property.propertyType}</span>
              </div>
              <div className="pd-detail-row">
                <span className="pd-detail-label">Listed For</span>
                <span className="pd-detail-val pd-capitalize">{property.purpose}</span>
              </div>
              {property.bedrooms && (
                <div className="pd-detail-row">
                  <span className="pd-detail-label">Bedrooms</span>
                  <span className="pd-detail-val">{property.bedrooms}</span>
                </div>
              )}
              {property.bathrooms && (
                <div className="pd-detail-row">
                  <span className="pd-detail-label">Bathrooms</span>
                  <span className="pd-detail-val">{property.bathrooms}</span>
                </div>
              )}
              {property.area && (
                <div className="pd-detail-row">
                  <span className="pd-detail-label">Carpet Area</span>
                  <span className="pd-detail-val">{property.area.toLocaleString()} sq.ft</span>
                </div>
              )}
              {property.furnishedStatus && (
                <div className="pd-detail-row">
                  <span className="pd-detail-label">Furnished Status</span>
                  <span className="pd-detail-val pd-capitalize">{property.furnishedStatus}</span>
                </div>
              )}
              {property.city && (
                <div className="pd-detail-row">
                  <span className="pd-detail-label">City</span>
                  <span className="pd-detail-val">{property.city}</span>
                </div>
              )}
              <div className="pd-detail-row">
                <span className="pd-detail-label">Listing Status</span>
                <span className="pd-detail-val pd-capitalize">{property.status || "Active"}</span>
              </div>
              {property.createdAt && (
                <div className="pd-detail-row">
                  <span className="pd-detail-label">Posted On</span>
                  <span className="pd-detail-val">
                    {new Date(property.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric", month: "short", year: "numeric",
                    })}
                  </span>
                </div>
              )}
              {lat && lng && (
                <div className="pd-detail-row">
                  <span className="pd-detail-label">Coordinates</span>
                  <span className="pd-detail-val">{lat.toFixed(4)}, {lng.toFixed(4)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {property.description && (
            <div className="pd-section">
              <h2 className="pd-section-title">Description</h2>
              <p className="pd-description">{property.description}</p>
            </div>
          )}

          {/* Map */}
          {lat && lng && (
            <div className="pd-section">
              <h2 className="pd-section-title">Location on Map</h2>
              <div className="pd-map-wrap">
                <MapContainer
                  center={[lat, lng]}
                  zoom={14}
                  style={{ width: "100%", height: "270px", borderRadius: "10px" }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[lat, lng]}>
                    <Popup>{property.title}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          )}

          {/* Similar Properties */}
          {similarProperties.length > 0 && (
            <div className="pd-section">
              <h2 className="pd-section-title">
                Similar Properties in {property.city}
              </h2>
              <div className="pd-similar-grid">
                {similarProperties.map((p) => (
                  <div
                    key={p._id}
                    className="pd-similar-card"
                    onClick={() => navigate(`/property/${p._id}`)}
                  >
                    <div className="pd-similar-img-wrap">
                      {p.images?.[0] ? (
                        <img
                          src={p.images[0]}
                          alt={p.title}
                          className="pd-similar-img"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="pd-similar-placeholder">⌂</div>
                      )}
                      <span className={`pd-similar-badge ${purposeBadgeClass(p.purpose)}`}>
                        {p.purpose?.toUpperCase()}
                      </span>
                    </div>
                    <div className="pd-similar-body">
                      <p className="pd-similar-price">{formatPrice(p.price)}</p>
                      <p className="pd-similar-title">{p.title}</p>
                      <div className="pd-similar-meta">
                        {p.bedrooms  && <span>{p.bedrooms} bd</span>}
                        {p.bathrooms && <span>{p.bathrooms} ba</span>}
                        {p.area      && <span>{p.area.toLocaleString()} sqft</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ════ SIDEBAR ════ */}
        <div className="pd-sidebar">

          {/* Contact Card */}
          <div className="pd-contact-card">
            <h3 className="pd-contact-title">Interested in this property?</h3>
            <p className="pd-contact-sub">Get in touch with our verified agents</p>

            {/* Call Now — with agent panel */}
            <div className="pd-call-wrap" ref={agentPanelRef}>
              <button
                className="pd-btn pd-btn-primary"
                onClick={() => setShowAgentPanel((v) => !v)}
              >
                <Phone size={16} /> Call Now
              </button>

              {showAgentPanel && (
                <div className="pd-agent-panel">
                  <div className="pd-agent-panel-head">
                    <span>Agents in {property.city}</span>
                    <button
                      className="pd-agent-panel-close"
                      onClick={() => setShowAgentPanel(false)}
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {cityAgents.length === 0 ? (
                    <p className="pd-agent-empty">
                      No agents listed for {property.city} yet.
                    </p>
                  ) : (
                    <div className="pd-agent-list">
                      {cityAgents.map((agent) => (
                        <div key={agent.id} className="pd-agent-item">
                          <img
                            src={agent.photo}
                            alt={agent.name}
                            className="pd-agent-photo"
                          />
                          <div className="pd-agent-info">
                            <p className="pd-agent-name">{agent.name}</p>
                            <p className="pd-agent-city">
                              <MapPin size={11} /> {agent.city}
                            </p>
                            <p className="pd-agent-bio">{agent.bio}</p>
                            <a
                              href={`tel:${agent.phone}`}
                              className="pd-agent-phone"
                            >
                              <Phone size={12} /> {agent.phone}
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Send Email */}
            <button className="pd-btn pd-btn-secondary" onClick={handleSendEmail}>
              <Mail size={16} /> Send Email
            </button>

            {/* Schedule Visit */}
            <div className="pd-schedule">
              <h4 className="pd-schedule-title">Schedule a Visit</h4>
              <input
                type="date"
                className="pd-date-input"
                value={visitDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setVisitDate(e.target.value)}
              />
              <button
                className="pd-schedule-btn"
                onClick={handleScheduleVisit}
                disabled={!visitDate}
              >
                Schedule Tour
              </button>
            </div>
          </div>

          {/* Price Summary Card */}
          <div className="pd-price-card">
            <h4 className="pd-price-card-title">Price Summary</h4>
            <div className="pd-price-summary-row">
              <span>Property Price</span>
              <span className="pd-price-summary-val">{formatPrice(property.price)}</span>
            </div>
            {property.purpose === "rent" && (
              <>
                <div className="pd-price-summary-row">
                  <span>Security Deposit</span>
                  <span>{formatPrice(property.price * 2)}</span>
                </div>
                <div className="pd-price-summary-row pd-price-total">
                  <span>Total (First Month)</span>
                  <span>{formatPrice(property.price * 3)}</span>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default PropertyDetails;